import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, ensureDate, normalizarComanda, toCuiabaTimestamp } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Avatar, HeroCard, StatusBadge, KpiTile } from '../components/ui';
import api from '../services/api';
import { RefreshCcw, Plus, Clock, CheckCircle, DollarSign, CreditCard, Edit2 } from 'lucide-react';

const TZ_CUIABA = 'America/Cuiaba';

const ComandasPage = ({ abrirModalDireto = false, onNovaComandaConsumida }) => {
  const { user } = useAuth();
  const { comandas, setComandas, servicos, barbeiros, carregarDados } = useData();
  const [modal, setModal] = useState(false);
  const [modalPag, setModalPag] = useState(false);
  const [cmdSel, setCmdSel] = useState(null);
  const [comandaEmEdicao, setComandaEmEdicao] = useState(null);
  const [cliente, setCliente] = useState('');
  const [brbId, setBrbId] = useState(user?.tipo === 'barbeiro' ? user.barbeiroId : '');
  const [srvSel, setSrvSel] = useState([]);
  const srvSelRef = useRef([]);
  const [observacoes, setObservacoes] = useState('');
  const [formaPag, setFormaPag] = useState('');

  useEffect(() => { srvSelRef.current = srvSel; }, [srvSel]);

  const [salvando, setSalvando] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [novasComandas, setNovasComandas] = useState(0);
  const comandasLengthRef = useRef(0);

  const buscarComandas = useCallback(async (manual = false) => {
    if (!manual && (modal || modalPag)) return;
    if (!user) return;
    if (manual) setAtualizando(true);
    try {
      const comandasDados = await api.comandas.getAll();
      const comandasNormalizadas = comandasDados.map((c) => normalizarComanda(c));
      const qtdAnterior = comandasLengthRef.current;
      const qtdNova = comandasNormalizadas.length;
      comandasLengthRef.current = qtdNova;
      if (qtdNova > qtdAnterior && qtdAnterior > 0) {
        setNovasComandas(qtdNova - qtdAnterior);
        setTimeout(() => setNovasComandas(0), 3000);
      }
      setComandas(comandasNormalizadas);
    } catch (error) {
      if (error.isAuthError || error.response?.status === 401) return;
      console.error('Erro ao buscar comandas:', error);
    } finally {
      if (manual) setAtualizando(false);
    }
  }, [setComandas, modal, modalPag, user]);

  useEffect(() => {
    if (modal || modalPag) return;
    const intervalo = setInterval(() => buscarComandas(false), 3000);
    return () => clearInterval(intervalo);
  }, [buscarComandas, modal, modalPag]);

  const resetFormularioComanda = useCallback(() => {
    setCliente(''); setComandaEmEdicao(null); setObservacoes(''); setSrvSel([]);
    setBrbId(user?.tipo === 'barbeiro' ? user.barbeiroId : '');
  }, [user?.tipo, user?.barbeiroId]);

  const handleSalvarComanda = async () => {
    if (!cliente || !brbId || srvSel.length === 0) return;
    setSalvando(true);
    try {
      const barbeiroSelecionado = barbeiros.find(b => b.id === brbId);
      const srvs = srvSel.map((id) => {
        const s = servicos.find((sv) => sv.id === id);
        return { id: s?.id || id, nome: s?.nome, preco: parseFloat(s?.preco || 0), comissao: parseFloat(s?.comissao || 0) };
      });
      const total = srvs.reduce((sum, s) => sum + s.preco, 0);
      const payload = { nomeCliente: cliente, barbeiroId: brbId, servicos: srvs, observacoes, total };
      const modoEdicao = Boolean(comandaEmEdicao);
      const resposta = modoEdicao
        ? await api.comandas.update(comandaEmEdicao.id, payload)
        : await api.comandas.create(payload);
      const comandaNormalizada = normalizarComanda(resposta, { barbeiroNome: barbeiroSelecionado?.nome || resposta?.barbeiroNome });
      setComandas((prev) => {
        if (modoEdicao) return prev.map((item) => item.id === comandaNormalizada.id ? comandaNormalizada : item);
        return [...prev, comandaNormalizada];
      });
      document.body.style.overflow = '';
      setModal(false); resetFormularioComanda();
    } catch (error) {
      console.error('ERRO:', error);
      alert((comandaEmEdicao ? 'Erro ao atualizar comanda: ' : 'Erro ao criar comanda: ') + (error.response?.data?.message || 'Erro desconhecido'));
    } finally { setSalvando(false); }
  };

  const obterIdsServicos = (servicosComanda = []) => {
    return servicosComanda.map((s) => s?.id || s?.servicoId || null).filter(Boolean).map((id) => String(id));
  };

  const abrirModalNovaComanda = useCallback(() => {
    resetFormularioComanda(); setModal(true); document.body.style.overflow = 'hidden';
  }, [resetFormularioComanda]);

  const prevAbrirModalDireto = useRef(false);
  useEffect(() => {
    if (abrirModalDireto && !prevAbrirModalDireto.current) {
      abrirModalNovaComanda(); onNovaComandaConsumida?.();
    }
    prevAbrirModalDireto.current = abrirModalDireto;
  }, [abrirModalDireto, abrirModalNovaComanda]);

  const abrirModalEdicao = (comanda) => {
    setComandaEmEdicao(comanda); setModal(true); document.body.style.overflow = 'hidden';
    setCliente(comanda.nomeCliente || ''); setObservacoes(comanda.observacoes || '');
    setBrbId(comanda.barbeiroId || (user?.tipo === 'barbeiro' ? user.barbeiroId : ''));
    setSrvSel(obterIdsServicos(comanda.servicos));
  };

  const podeEditarComanda = (comanda) => {
    if (!comanda || comanda.status !== 'pendente') return false;
    if (user?.tipo === 'master' || user?.tipo === 'gestor') return true;
    if (user?.tipo === 'barbeiro') return comanda.barbeiroId === user.barbeiroId;
    return false;
  };

  const handlePagar = async () => {
    if (!formaPag) return;
    setPagando(true);
    try {
      const response = await api.comandas.pagar(cmdSel.id, formaPag);
      setComandas(prev => prev.map(c => c.id === cmdSel.id ? normalizarComanda({ ...c, status: 'pago', formaPagamento: formaPag }) : c));
      if (cmdSel.barbeiroId) {
        const saldoAtualizado = response?.barbeiro?.saldoDisponivel ?? response?.carteira?.saldo;
        window.dispatchEvent(new CustomEvent('walletBalanceUpdated', {
          detail: { barbeiroId: cmdSel.barbeiroId, saldoDisponivel: saldoAtualizado }
        }));
      }
      setModalPag(false); setCmdSel(null); setFormaPag('');
    } catch (error) {
      console.error('ERRO AO PAGAR:', error);
      alert('Erro ao processar pagamento: ' + (error.response?.data?.message || 'Erro desconhecido'));
    } finally { setPagando(false); }
  };

  const comandasVisiveis = user?.tipo === 'barbeiro' ? comandas.filter(c => c.barbeiroId === user.barbeiroId) : comandas;
  const DATA_LIMITE = ensureDate('2025-11-10T00:00:00-04:00');
  const comandasFiltradasPorData = comandasVisiveis.filter((c) => {
    const dataComanda = ensureDate(c.dataLancamentoISO || c.createdAt || c.data);
    return dataComanda >= DATA_LIMITE;
  });
  const comandasOrdenadas = [...comandasFiltradasPorData].sort((a, b) => {
    const dataA = ensureDate(a.dataLancamentoISO || a.data || a.createdAt);
    const dataB = ensureDate(b.dataLancamentoISO || b.data || b.createdAt);
    return dataB - dataA;
  });

  const now = new Date();
  const partsMes = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit' }).formatToParts(now);
  const anoMes = parseInt(partsMes.find((p) => p.type === 'year').value, 10);
  const mesMes = parseInt(partsMes.find((p) => p.type === 'month').value, 10);
  const inicioMesVigente = new Date(Date.UTC(anoMes, mesMes - 1, 1, 0, 0, 0, 0));
  const ultimoDiaMes = new Date(Date.UTC(anoMes, mesMes, 0)).getUTCDate();
  const fimMesVigente = new Date(Date.UTC(anoMes, mesMes - 1, ultimoDiaMes, 23, 59, 59, 999));
  const comandasMesVigente = comandasFiltradasPorData.filter((c) => {
    const dataComanda = ensureDate(c.dataLancamentoISO || c.createdAt || c.data);
    return dataComanda >= inicioMesVigente && dataComanda <= fimMesVigente;
  });

  const pend = comandasOrdenadas.filter(c => c.status === 'pendente');
  const pagas = comandasMesVigente.filter(c => c.status === 'pago');
  const totalReceita = pagas.reduce((sum, c) => sum + (parseFloat(c.total) || 0), 0);

  return (
    <div style={{ padding: '0 0 40px', minHeight: '100dvh' }}>
      {novasComandas > 0 && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '12px 20px',
          borderRadius: 14, background: T.success, color: '#fff', fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 20px rgba(48,209,88,0.4)',
        }}>{novasComandas} nova{novasComandas > 1 ? 's' : ''} comanda{novasComandas > 1 ? 's' : ''}!</div>
      )}

      <LargeTitle
        title="Comandas"
        subtitle="Atendimentos e pagamentos"
        trailing={
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => buscarComandas(true)} disabled={atualizando}
              style={{
                border: 'none', cursor: 'pointer', width: 38, height: 38, borderRadius: 19,
                background: T.fill, color: T.label, display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: atualizando ? 0.6 : 1,
              }}>
              <RefreshCcw size={18} className={atualizando ? 'animate-spin' : ''} />
            </button>
            {(user?.tipo === 'gestor' || user?.tipo === 'barbeiro' || user?.tipo === 'master') && (
              <button onClick={abrirModalNovaComanda} style={{
                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 14, background: T.accent, color: '#fff',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                boxShadow: '0 4px 12px rgba(10,132,255,0.4)',
              }}><Plus size={18} /> Nova</button>
            )}
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '0 20px' }}>
        <KpiTile icon={Clock} tint={T.warning} label="Pendentes" value={pend.length} sub={user?.tipo === 'gestor' ? fmtBRL(pend.reduce((s, c) => s + (parseFloat(c.total) || 0), 0)) : `${pend.length} cmd`} />
        <KpiTile icon={CheckCircle} tint={T.success} label="Pagas" value={pagas.length} sub={fmtBRL(pagas.reduce((s, c) => s + (parseFloat(c.total) || 0), 0))} />
        <KpiTile icon={DollarSign} tint={T.accent} label="Receita" value={fmtBRL(totalReceita)} sub="do mes" />
      </div>

      {pend.length > 0 && (
        <div style={{ padding: '0 20px' }}>
          <SectionHeader title="Pendentes" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pend.map((cmd) => (
              <SolidCard key={cmd.id} padding={16} radius={18}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: T.label }}>{cmd.nomeCliente}</div>
                    <div style={{ fontSize: 12, color: T.label2, marginTop: 2 }}>Barbeiro: {cmd.barbeiroNome}</div>
                    {cmd.dataLancamento && <div style={{ fontSize: 11, color: T.label3, marginTop: 2 }}>{cmd.dataLancamento} {cmd.horaLancamento && `as ${cmd.horaLancamento}`}</div>}
                  </div>
                  <StatusBadge status="pendente" label="PENDENTE" />
                </div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cmd.servicos.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.label2 }}>
                      <span>{s.nome}</span>
                      <span className="tnum" style={{ fontWeight: 600 }}>{fmtBRL(parseFloat(s.preco || 0))}</span>
                    </div>
                  ))}
                </div>
                {cmd.observacoes && <div style={{ fontSize: 12, color: T.label3, marginTop: 6, padding: '6px 10px', background: T.fill, borderRadius: 10 }}>{cmd.observacoes}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: `0.5px solid ${T.separator}` }}>
                  <div className="tnum" style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>{fmtBRL(parseFloat(cmd.total) || 0)}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {podeEditarComanda(cmd) && (user?.tipo === 'gestor' || user?.tipo === 'master') && (
                      <button onClick={() => abrirModalEdicao(cmd)} style={{
                        border: `1px solid ${T.accent}`, background: 'transparent', cursor: 'pointer',
                        padding: '8px 14px', borderRadius: 12, color: T.accent, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}><Edit2 size={14} /> Editar</button>
                    )}
                    {(user?.tipo === 'gestor' || user?.tipo === 'caixa') && (
                      <button onClick={() => { setCmdSel(cmd); setModalPag(true); }} style={{
                        border: 'none', cursor: 'pointer', padding: '8px 18px', borderRadius: 12,
                        background: T.success, color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 4,
                        boxShadow: '0 4px 12px rgba(48,209,88,0.3)',
                      }}><CreditCard size={14} /> Pagar</button>
                    )}
                  </div>
                </div>
              </SolidCard>
            ))}
          </div>
        </div>
      )}

      {/* Modal Nova/Editar Comanda — iOS Page Sheet */}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50,
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        opacity: modal ? 1 : 0, pointerEvents: modal ? 'auto' : 'none',
        transition: 'opacity 320ms ease',
      }} onClick={() => { document.body.style.overflow = ''; setModal(false); resetFormularioComanda(); }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, top: 44, zIndex: 51,
        borderRadius: '20px 20px 0 0',
        transform: modal ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 420ms cubic-bezier(0.32, 0.72, 0, 1)',
        background: T.bg,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ flexShrink: 0, background: T.surface, borderBottom: `0.5px solid ${T.separator}` }}>
          <div style={{ width: 36, height: 5, borderRadius: 3, background: T.label3, margin: '10px auto 0', opacity: 0.4 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 16px' }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: T.label, letterSpacing: '-0.025em' }}>
                {comandaEmEdicao ? 'Editar Comanda' : 'Nova Comanda'}
              </div>
              {comandaEmEdicao ? (
                <div style={{ fontSize: 13, color: T.label3, marginTop: 2 }}>Ultimo lancamento: {comandaEmEdicao.dataLancamento} as {comandaEmEdicao.horaLancamento}</div>
              ) : (
                <div style={{ fontSize: 14, color: T.label2, marginTop: 2, letterSpacing: '-0.01em' }}>Preencha os dados do atendimento</div>
              )}
            </div>
            <button onClick={() => { document.body.style.overflow = ''; setModal(false); resetFormularioComanda(); }} style={{
              width: 32, height: 32, borderRadius: 16, border: 'none', cursor: 'pointer',
              background: T.fill, color: T.label2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          <div style={{ padding: '20px 20px 0' }}>
            {/* Cliente */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8, letterSpacing: '-0.01em', textTransform: 'uppercase', paddingLeft: 4 }}>Cliente</label>
              <input type="text" value={cliente || ''} onChange={(e) => setCliente(e.target.value)} placeholder="Nome do cliente"
                autoComplete="off" autoCapitalize="words"
                onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                style={{
                  width: '100%', padding: '16px 18px', borderRadius: 14, border: 'none',
                  background: T.surface, color: T.label, fontSize: 17, fontFamily: 'inherit',
                  fontWeight: 500, outline: 'none', letterSpacing: '-0.01em',
                  boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px ${T.glassRing}`,
                }} />
            </div>

            {/* Barbeiro */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8, letterSpacing: '-0.01em', textTransform: 'uppercase', paddingLeft: 4 }}>Barbeiro</label>
              {user?.tipo === 'barbeiro' ? (
                <div style={{
                  padding: '16px 18px', borderRadius: 14, background: T.surface,
                  fontSize: 17, fontWeight: 500, color: T.label, letterSpacing: '-0.01em',
                  boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px ${T.glassRing}`,
                }}>{barbeiros.find(b => b.id === user.barbeiroId)?.nome || 'Voce'}</div>
              ) : (
                <select value={brbId} onChange={(e) => setBrbId(e.target.value)} style={{
                  width: '100%', padding: '16px 18px', borderRadius: 14, border: 'none',
                  background: T.surface, color: brbId ? T.label : T.label3, fontSize: 17,
                  fontFamily: 'inherit', fontWeight: 500, outline: 'none', letterSpacing: '-0.01em',
                  boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px ${T.glassRing}`,
                  WebkitAppearance: 'none', appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%233C3C4399' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center',
                }}>
                  <option value="">Selecione o barbeiro</option>
                  {barbeiros.filter(b => b.ativo || String(b.id) === String(brbId)).map(b => (
                    <option key={b.id} value={b.id}>{b.nome}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Servicos — iOS grouped list style */}
          <div style={{ padding: '0 20px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8, letterSpacing: '-0.01em', textTransform: 'uppercase', paddingLeft: 4 }}>
              Servicos {srvSel.length > 0 && <span style={{ color: T.accent }}>({srvSel.length})</span>}
            </label>
            <div style={{
              borderRadius: 16, overflow: 'hidden', background: T.surface,
              boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px ${T.glassRing}`,
            }}>
              {servicos.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.label2, fontSize: 15 }}>Carregando servicos...</div>
              ) : servicos.filter(s => s.ativo).length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.label2, fontSize: 15 }}>Nenhum servico ativo</div>
              ) : (
                servicos.filter(s => s.ativo).map((s, idx, arr) => {
                  const selected = srvSel.includes(s.id);
                  return (
                    <div key={s.id} onClick={() => {
                      if (selected) setSrvSel(srvSel.filter(id => id !== s.id));
                      else setSrvSel([...srvSel, s.id]);
                    }} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '15px 18px', cursor: 'pointer',
                      background: selected ? 'rgba(10,132,255,0.06)' : 'transparent',
                      borderBottom: idx < arr.length - 1 ? `0.5px solid ${T.separator}` : 'none',
                      transition: 'background 150ms ease',
                    }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                        background: selected ? T.accent : 'transparent',
                        border: `2px solid ${selected ? T.accent : T.label3}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 200ms cubic-bezier(0.32, 0.72, 0, 1)',
                        boxShadow: selected ? '0 2px 8px rgba(10,132,255,0.3)' : 'none',
                      }}>
                        {selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <span style={{ flex: 1, fontSize: 17, fontWeight: 500, color: T.label, letterSpacing: '-0.01em' }}>{s.nome}</span>
                      <span className="tnum" style={{ fontSize: 17, fontWeight: 600, color: T.accent, letterSpacing: '-0.01em' }}>{fmtBRL(parseFloat(s.preco || 0))}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Observacoes */}
          <div style={{ padding: '20px 20px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8, letterSpacing: '-0.01em', textTransform: 'uppercase', paddingLeft: 4 }}>Observacoes</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} placeholder="Informacoes adicionais (opcional)"
              style={{
                width: '100%', padding: '16px 18px', borderRadius: 14, border: 'none',
                background: T.surface, color: T.label, fontSize: 16, fontFamily: 'inherit',
                fontWeight: 400, outline: 'none', minHeight: 90, resize: 'none',
                boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px ${T.glassRing}`,
                letterSpacing: '-0.01em',
              }} />
          </div>

          <div style={{ height: 20 }} />
        </div>

        {/* Sticky footer */}
        <div style={{
          flexShrink: 0, background: T.surface,
          borderTop: `0.5px solid ${T.separator}`,
          padding: '12px 20px calc(12px + env(safe-area-inset-bottom, 16px))',
        }}>
          {srvSel.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 14, padding: '14px 18px', borderRadius: 14,
              background: `linear-gradient(135deg, rgba(10,132,255,0.08) 0%, rgba(94,92,230,0.06) 100%)`,
              border: '0.5px solid rgba(10,132,255,0.12)',
            }}>
              <span style={{ fontWeight: 600, color: T.label, fontSize: 15, letterSpacing: '-0.01em' }}>
                Total · {srvSel.length} {srvSel.length === 1 ? 'servico' : 'servicos'}
              </span>
              <span className="tnum" style={{ fontSize: 24, fontWeight: 700, color: T.accent, letterSpacing: '-0.025em' }}>
                {fmtBRL(srvSel.reduce((sum, id) => { const s = servicos.find(sv => sv.id === id); return sum + parseFloat(s?.preco || 0); }, 0))}
              </span>
            </div>
          )}
          <button onClick={handleSalvarComanda} disabled={salvando || !cliente || !brbId || srvSel.length === 0}
            style={{
              width: '100%', padding: '17px 0', borderRadius: 16, border: 'none',
              background: (salvando || !cliente || !brbId || srvSel.length === 0)
                ? T.fill2
                : 'linear-gradient(135deg, #0A84FF 0%, #0071E3 100%)',
              color: '#fff', fontFamily: 'inherit', fontSize: 17, fontWeight: 600,
              cursor: (salvando || !cliente || !brbId || srvSel.length === 0) ? 'not-allowed' : 'pointer',
              boxShadow: (salvando || !cliente || !brbId || srvSel.length === 0) ? 'none' : '0 4px 16px rgba(10,132,255,0.4), 0 1px 0 rgba(255,255,255,0.15) inset',
              letterSpacing: '-0.01em',
              transition: 'all 200ms ease',
            }}>
            {salvando ? 'Salvando...' : comandaEmEdicao ? 'Atualizar Comanda' : 'Enviar Comanda'}
          </button>
        </div>
      </div>

      {/* Modal Pagamento */}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50,
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        opacity: (modalPag && cmdSel) ? 1 : 0, pointerEvents: (modalPag && cmdSel) ? 'auto' : 'none',
        transition: 'opacity 280ms ease',
      }} onClick={() => { setModalPag(false); setCmdSel(null); setFormaPag(''); }} />
      <div style={{
        position: 'fixed', left: 8, right: 8, bottom: 8, zIndex: 51,
        borderRadius: 38, maxHeight: '85vh',
        transform: (modalPag && cmdSel) ? 'translateY(0)' : 'translateY(110%)',
        transition: 'transform 360ms cubic-bezier(0.32, 0.72, 0, 1)',
        background: 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(40px) saturate(180%)', WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        border: '0.5px solid rgba(255,255,255,0.8)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px 16px', flexShrink: 0 }}>
          <div style={{ width: 36, height: 5, borderRadius: 3, background: T.label3, margin: '0 auto 14px' }} />
          <div style={{ fontSize: 22, fontWeight: 700, color: T.label, letterSpacing: '-0.02em' }}>Receber Pagamento</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 20px 20px', WebkitOverflowScrolling: 'touch' }}>
          {cmdSel && (
            <>
              <SolidCard padding={16} radius={18} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, color: T.label2, fontWeight: 500 }}>Cliente</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: T.label, marginTop: 2 }}>{cmdSel.nomeCliente}</div>
                {cmdSel.dataLancamento && <div style={{ fontSize: 11, color: T.label3, marginTop: 4 }}>{cmdSel.dataLancamento} {cmdSel.horaLancamento && `as ${cmdSel.horaLancamento}`}</div>}
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {cmdSel.servicos.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.label2 }}>
                      <span>{s.nome}</span><span className="tnum" style={{ fontWeight: 600 }}>{fmtBRL(parseFloat(s.preco || 0))}</span>
                    </div>
                  ))}
                </div>
                {cmdSel.observacoes && <div style={{ fontSize: 12, color: T.label3, marginTop: 8, padding: '8px 12px', background: T.fill, borderRadius: 12 }}>{cmdSel.observacoes}</div>}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `0.5px solid ${T.separator}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: T.label }}>Total</span>
                  <span className="tnum" style={{ fontSize: 24, fontWeight: 700, color: T.accent, letterSpacing: '-0.02em' }}>{fmtBRL(parseFloat(cmdSel.total) || 0)}</span>
                </div>
              </SolidCard>

              <div style={{ fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8 }}>Forma de Pagamento</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Dinheiro', 'Cartao Debito', 'Cartao Credito', 'PIX'].map((forma) => {
                  const selected = formaPag === forma;
                  return (
                    <div key={forma} onClick={() => setFormaPag(forma)} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderRadius: 16, cursor: 'pointer',
                      background: selected ? 'rgba(48,209,88,0.08)' : T.surface,
                      border: `1.5px solid ${selected ? T.success : T.separator}`,
                      boxShadow: selected ? '0 2px 8px rgba(48,209,88,0.15)' : '0 1px 3px rgba(0,0,0,0.04)',
                      transition: 'all 180ms ease',
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 11, flexShrink: 0,
                        background: selected ? T.success : 'transparent',
                        border: `2px solid ${selected ? T.success : T.label3}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 180ms ease',
                      }}>
                        {selected && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#fff' }} />}
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 600, color: T.label }}>{forma}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '16px 20px 28px', display: 'flex', gap: 10, flexShrink: 0, borderTop: `0.5px solid ${T.separator}` }}>
          <button onClick={() => { setModalPag(false); setCmdSel(null); setFormaPag(''); }} style={{
            flex: 1, padding: '16px 0', borderRadius: 16, border: `1px solid ${T.separator}`, background: T.surface,
            color: T.label, fontFamily: 'inherit', fontSize: 16, fontWeight: 600, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={handlePagar} disabled={pagando || !formaPag} style={{
            flex: 1.4, padding: '16px 0', borderRadius: 16, border: 'none',
            background: (pagando || !formaPag) ? T.fill2 : T.success,
            color: '#fff', fontFamily: 'inherit', fontSize: 16, fontWeight: 600,
            cursor: (pagando || !formaPag) ? 'not-allowed' : 'pointer',
            boxShadow: (pagando || !formaPag) ? 'none' : '0 4px 14px rgba(48,209,88,0.35)',
          }}>{pagando ? 'Processando...' : 'Confirmar Pagamento'}</button>
        </div>
      </div>
    </div>
  );
};

export default ComandasPage;
